import { useCallback } from 'react';
import { useNavigate as useReactRouterNavigate } from 'react-router-dom';
import { useUIStore } from '../store/uiStore';

/**
 * Custom navigation hook that integrates React Router with the navigation lock system.
 * This hook respects the navigation lock and shows a confirmation dialog when needed.
 */
export function useNavigateWithLock() {
  const navigate = useReactRouterNavigate();

  const navigateWithLock = useCallback(
    (to: string | number, options?: { replace?: boolean; state?: any }) => {
      // Get current navigation lock state
      const state = useUIStore.getState();
      const navigationLock = state.navigationLock;

      // Handle browser back/forward (number)
      if (typeof to === 'number') {
        if (navigationLock.isLocked) {
          // Store pending navigation for browser history
          useUIStore.setState((state) => ({
            navigationLock: {
              ...state.navigationLock,
              pendingNavigation: { 
                path: to, // Store as number for history navigation
                type: 'history' as const
              }
            },
            modals: {
              ...state.modals,
              navigationConfirm: true
            }
          }));
          return;
        }
        navigate(to);
        return;
      }

      // Ensure absolute path
      const absolutePath = to.startsWith('/') ? to : `/${to}`;

      // Check if navigation is locked
      if (navigationLock.isLocked) {
        // Store the pending navigation and show confirmation dialog
        useUIStore.setState((state) => ({
          navigationLock: {
            ...state.navigationLock,
            pendingNavigation: { 
              path: absolutePath,
              options,
              type: 'path' as const
            }
          },
          modals: {
            ...state.modals,
            navigationConfirm: true
          }
        }));
        return;
      }

      // Proceed with navigation
      if (options?.replace) {
        navigate(absolutePath, { replace: true, state: options?.state });
      } else {
        navigate(absolutePath, { state: options?.state });
      }
    },
    [navigate]
  );

  return navigateWithLock;
}

